(function () {
  "use strict";

  var READY = "data-cha-image-viewer-ready";
  var TRIGGER_SELECTOR = 'img[' + READY + '=\"true\"],svg[' + READY + '=\"true\"]';
  var EXCLUDED_ANCESTORS = [
    "media-gallery", ".media-gallery", ".product-card", ".product-card-mini", ".thumbnail", ".thumbnail-list",
    ".logo-list", ".header", ".footer", "header", "footer", "nav", ".payment-icons", ".social-links",
    ".swatch", ".rating", ".modal", ".popup", "button", "label", "[aria-hidden=\"true\"]", "[data-cha-image-viewer=\"off\"]"
  ].join(",");
  var EXCLUDED_CLASS = /(logo|icon|badge|avatar|swatch|payment|rating|thumbnail|spinner|placeholder|flag|emoji)/i;
  var GROUP_SELECTORS = [
    "[data-cha-image-group]", ".cha-pv-performer-pattern-lab", ".cha-ryb-pattern-pair", ".ckb-article-reader",
    ".cha-wire-workshop", "[data-ryb-workshop]", ".rte", ".cha-product-content", "article", "section"
  ];
  var groupIds = new WeakMap();
  var nextGroupId = 1;
  var observerActive = false;
  var viewer;
  var stage;
  var fullImage;
  var caption;
  var counter;
  var zoomReadout;
  var originalLink;
  var previousButton;
  var nextButton;
  var closeButton;
  var triggers = [];
  var activeIndex = 0;
  var lastTrigger = null;
  var scale = 1;
  var panX = 0;
  var panY = 0;
  var pointers = new Map();
  var pinchDistance = 0;
  var pinchScale = 1;
  var dragOrigin = null;

  function isImageUrl(value) {
    return /\.(?:avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?$/i.test(value || "");
  }

  function triggerAlt(trigger) {
    if (trigger instanceof HTMLImageElement) return trigger.alt || "image detail";
    return trigger.querySelector("title")?.textContent?.trim() || trigger.getAttribute("aria-label") || "NEC pattern detail";
  }

  function svgSource(svg) {
    var clone = svg.cloneNode(true);
    if (!clone.getAttribute("xmlns")) clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(new XMLSerializer().serializeToString(clone));
  }

  function bestSource(image) {
    var explicit = image.dataset.chaFullsize || image.closest("a[href]")?.href;
    if (explicit && isImageUrl(explicit)) return explicit;
    if (image instanceof SVGElement) return svgSource(image);
    var candidates = String(image.srcset || "").split(",").map(function (part) {
      var bits = part.trim().split(/\s+/);
      var descriptor = bits[1] || "1x";
      var score = descriptor.endsWith("w") ? Number.parseFloat(descriptor) : Number.parseFloat(descriptor) * 1000;
      return { url: bits[0], score: Number.isFinite(score) ? score : 0 };
    }).filter(function (item) { return item.url; }).sort(function (a, b) { return b.score - a.score; });
    var source = candidates[0]?.url || image.currentSrc || image.src;
    try {
      var url = new URL(source, location.href);
      if (/shopify|shopifypreview/.test(url.hostname) || /\/cdn\/shop\//.test(url.pathname)) {
        ["width", "height", "crop"].forEach(function (key) { url.searchParams.delete(key); });
      }
      return url.href;
    } catch (_) {
      return source;
    }
  }

  function groupKey(image) {
    var explicit = image.dataset.chaImageGroup;
    if (explicit) return "explicit:" + explicit;
    var root = null;
    GROUP_SELECTORS.some(function (selector) { root = image.closest(selector); return Boolean(root); });
    root = root || image.parentElement || document.querySelector(".main");
    if (!groupIds.has(root)) groupIds.set(root, "auto:" + nextGroupId++);
    return groupIds.get(root);
  }

  function eligible(image) {
    if (!(image instanceof HTMLImageElement) && !(image instanceof SVGElement)) return false;
    if (!image.closest(".main")) return false;
    if (image instanceof SVGElement) return Boolean(image.closest(".cha-nec2d-polar"));
    if (image.dataset.chaImageViewer === "on") return true;
    if (image.closest(EXCLUDED_ANCESTORS)) return false;
    if (EXCLUDED_CLASS.test(image.className || "")) return false;
    var link = image.closest("a[href]");
    if (link && !isImageUrl(link.href)) return false;
    if ((image.getAttribute("role") || "").toLowerCase() === "presentation") return false;
    return Boolean(image.alt || image.closest("figure") || image.closest(".rte,.cha-product-content,.ckb-article-reader,[data-ryb-workshop],.cha-wire-workshop"));
  }

  function prepare(image) {
    if (!eligible(image) || image.hasAttribute(READY)) return;
    image.setAttribute(READY, "true");
    image.dataset.chaImageGroupResolved = groupKey(image);
    image.tabIndex = image.tabIndex >= 0 ? image.tabIndex : 0;
    image.setAttribute("role", "button");
    image.setAttribute("aria-haspopup", "dialog");
    image.setAttribute("aria-label", "View full-size image: " + triggerAlt(image));
    if (!image.title) image.title = "View full-size image";
  }

  function eventTrigger(event) {
    var path = typeof event.composedPath === "function" ? event.composedPath() : [];
    for (var index = 0; index < path.length; index += 1) {
      if (path[index]?.matches?.(TRIGGER_SELECTOR)) return path[index];
    }
    return event.target.closest?.(TRIGGER_SELECTOR) || null;
  }

  function scan(root) {
    if (root instanceof HTMLImageElement || (root instanceof SVGElement && root.closest(".cha-nec2d-polar"))) prepare(root);
    if (root.querySelectorAll) root.querySelectorAll("img,.cha-nec2d-polar svg").forEach(prepare);
  }

  function createViewer() {
    viewer = document.createElement("div");
    viewer.className = "cha-image-viewer";
    viewer.hidden = true;
    viewer.setAttribute("role", "dialog");
    viewer.setAttribute("aria-modal", "true");
    viewer.setAttribute("aria-labelledby", "CHAImageViewerTitle");
    viewer.innerHTML = '<header class="cha-image-viewer__toolbar"><div class="cha-image-viewer__identity"><strong id="CHAImageViewerTitle">Full-size image viewer</strong><span data-cha-viewer-counter></span></div><div class="cha-image-viewer__controls"><button type="button" data-cha-viewer-zoom-out aria-label="Zoom out">−</button><button type="button" data-cha-viewer-reset aria-label="Reset zoom"><span data-cha-viewer-zoom>100%</span></button><button type="button" data-cha-viewer-zoom-in aria-label="Zoom in">+</button><a class="cha-image-viewer__original" data-cha-viewer-original target="_blank" rel="noopener">Open original ↗</a><button class="cha-image-viewer__close" type="button" data-cha-viewer-close aria-label="Close full-size image viewer">×</button></div></header><div class="cha-image-viewer__stage" data-cha-viewer-stage><button class="cha-image-viewer__nav cha-image-viewer__nav--prev" type="button" data-cha-viewer-prev aria-label="Previous image">‹</button><img class="cha-image-viewer__image" data-cha-viewer-image alt=""><button class="cha-image-viewer__nav cha-image-viewer__nav--next" type="button" data-cha-viewer-next aria-label="Next image">›</button></div><footer class="cha-image-viewer__footer"><p class="cha-image-viewer__caption" data-cha-viewer-caption></p><p class="cha-image-viewer__help">Wheel, double-click or pinch to zoom · Drag to inspect · Esc to close</p></footer>';
    document.body.appendChild(viewer);
    stage = viewer.querySelector("[data-cha-viewer-stage]");
    fullImage = viewer.querySelector("[data-cha-viewer-image]");
    caption = viewer.querySelector("[data-cha-viewer-caption]");
    counter = viewer.querySelector("[data-cha-viewer-counter]");
    zoomReadout = viewer.querySelector("[data-cha-viewer-zoom]");
    originalLink = viewer.querySelector("[data-cha-viewer-original]");
    previousButton = viewer.querySelector("[data-cha-viewer-prev]");
    nextButton = viewer.querySelector("[data-cha-viewer-next]");
    closeButton = viewer.querySelector("[data-cha-viewer-close]");
    viewer.querySelector("[data-cha-viewer-zoom-out]").addEventListener("click", function () { setZoom(scale - .5); });
    viewer.querySelector("[data-cha-viewer-zoom-in]").addEventListener("click", function () { setZoom(scale + .5); });
    viewer.querySelector("[data-cha-viewer-reset]").addEventListener("click", resetTransform);
    previousButton.addEventListener("click", function () { showAt(activeIndex - 1); });
    nextButton.addEventListener("click", function () { showAt(activeIndex + 1); });
    closeButton.addEventListener("click", close);
    viewer.addEventListener("click", function (event) { if (event.target === viewer) close(); });
    stage.addEventListener("click", function (event) { if (event.target === stage && scale === 1) close(); });
    stage.addEventListener("dblclick", function () { setZoom(scale > 1 ? 1 : 2.5); });
    stage.addEventListener("wheel", function (event) { event.preventDefault(); setZoom(scale + (event.deltaY < 0 ? .35 : -.35)); }, { passive: false });
    stage.addEventListener("pointerdown", pointerDown);
    stage.addEventListener("pointermove", pointerMove);
    stage.addEventListener("pointerup", pointerUp);
    stage.addEventListener("pointercancel", pointerUp);
  }

  function imageCaption(image) {
    return image.dataset.chaCaption || image.closest("figure")?.querySelector("figcaption")?.innerText || image.closest(".cha-pv-nec-case")?.querySelector(".cha-pv-nec-caption")?.innerText || triggerAlt(image) || "Full-size image";
  }

  function resetTransform() {
    scale = 1; panX = 0; panY = 0; applyTransform();
  }

  function setZoom(value) {
    scale = Math.min(8, Math.max(1, Math.round(value * 100) / 100));
    if (scale === 1) { panX = 0; panY = 0; }
    applyTransform();
  }

  function applyTransform() {
    fullImage.style.transform = "translate3d(" + panX + "px," + panY + "px,0) scale(" + scale + ")";
    zoomReadout.textContent = Math.round(scale * 100) + "%";
    stage.classList.toggle("is-zoomed", scale > 1);
  }

  function groupTriggers(trigger) {
    var key = trigger.dataset.chaImageGroupResolved;
    return Array.from(document.querySelectorAll(TRIGGER_SELECTOR)).filter(function (image) { return image.dataset.chaImageGroupResolved === key && image.getClientRects().length; });
  }

  function showAt(index) {
    if (!triggers.length) return;
    activeIndex = (index + triggers.length) % triggers.length;
    var source = triggers[activeIndex];
    resetTransform();
    fullImage.src = bestSource(source);
    fullImage.alt = triggerAlt(source);
    caption.textContent = imageCaption(source);
    counter.textContent = triggers.length > 1 ? "Image " + (activeIndex + 1) + " of " + triggers.length : "High-resolution detail";
    originalLink.href = bestSource(source);
    previousButton.hidden = triggers.length < 2;
    nextButton.hidden = triggers.length < 2;
  }

  function open(trigger) {
    if (!viewer) createViewer();
    lastTrigger = trigger;
    triggers = groupTriggers(trigger);
    activeIndex = Math.max(0, triggers.indexOf(trigger));
    viewer.hidden = false;
    document.documentElement.classList.add("cha-image-viewer-open");
    document.body.classList.add("cha-image-viewer-open");
    showAt(activeIndex);
    closeButton.focus();
    document.dispatchEvent(new CustomEvent("cha:image-viewer-open", { detail: { source: bestSource(trigger), alt: triggerAlt(trigger) } }));
  }

  function close() {
    if (!viewer || viewer.hidden) return;
    viewer.hidden = true;
    fullImage.removeAttribute("src");
    document.documentElement.classList.remove("cha-image-viewer-open");
    document.body.classList.remove("cha-image-viewer-open");
    resetTransform();
    if (lastTrigger?.isConnected) lastTrigger.focus();
  }

  function pointerDown(event) {
    stage.setPointerCapture(event.pointerId);
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.size === 1 && scale > 1) { dragOrigin = { x: event.clientX, y: event.clientY, panX: panX, panY: panY }; stage.classList.add("is-dragging"); }
    if (pointers.size === 2) {
      var values = Array.from(pointers.values());
      pinchDistance = Math.hypot(values[0].x - values[1].x, values[0].y - values[1].y);
      pinchScale = scale;
      dragOrigin = null;
      stage.classList.remove("is-dragging"); stage.classList.add("is-pinching");
    }
  }

  function pointerMove(event) {
    if (!pointers.has(event.pointerId)) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.size === 2) {
      var values = Array.from(pointers.values());
      var distance = Math.hypot(values[0].x - values[1].x, values[0].y - values[1].y);
      if (pinchDistance) setZoom(pinchScale * distance / pinchDistance);
    } else if (dragOrigin && scale > 1) {
      panX = dragOrigin.panX + event.clientX - dragOrigin.x;
      panY = dragOrigin.panY + event.clientY - dragOrigin.y;
      applyTransform();
    }
  }

  function pointerUp(event) {
    pointers.delete(event.pointerId);
    if (stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId);
    if (pointers.size < 2) stage.classList.remove("is-pinching");
    if (!pointers.size) { dragOrigin = null; stage.classList.remove("is-dragging"); }
  }

  document.addEventListener("click", function (event) {
    var image = eventTrigger(event);
    if (!image || event.defaultPrevented) return;
    event.preventDefault(); event.stopPropagation(); open(image);
  }, true);
  document.addEventListener("keydown", function (event) {
    var image = eventTrigger(event);
    if (image && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); open(image); return; }
    if (!viewer || viewer.hidden) return;
    if (event.key === "Escape") { event.preventDefault(); close(); }
    else if (event.key === "ArrowLeft") showAt(activeIndex - 1);
    else if (event.key === "ArrowRight") showAt(activeIndex + 1);
    else if (event.key === "+" || event.key === "=") setZoom(scale + .5);
    else if (event.key === "-") setZoom(scale - .5);
    else if (event.key === "Tab") {
      var focusable = Array.from(viewer.querySelectorAll('button:not([hidden]):not(:disabled),a[href]:not([hidden])'));
      if (!focusable.length) return;
      var first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });
  document.addEventListener("load", function (event) { if (event.target instanceof HTMLImageElement) prepare(event.target); }, true);
  var observer = new MutationObserver(function (mutations) { mutations.forEach(function (mutation) { mutation.addedNodes.forEach(scan); }); });
  window.ChameleonImageViewer = {
    version: "1.2.0",
    refresh: function (root) { scan(root || document); },
    open: function (image) { if (eligible(image)) { prepare(image); open(image); } }
  };
  function init() {
    scan(document);
    if (observerActive) return;
    var observationRoot = document.body || document.documentElement;
    if (!observationRoot) return;
    observer.observe(observationRoot, { childList: true, subtree: true });
    observerActive = true;
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true }); else init();
})();
